import { Select } from '@mantine/core';
import { size } from 'lodash/fp';
import React, { useEffect } from 'react';
import { usePrevious } from 'react-use';

import { useClaimableDevices } from './hooks';

interface ModelFieldProps {
  partnerId: string;
  value: string;
  fieldName: string;
  setFieldValue: (name: string, value: string) => void;
}

export function ModelField({
  partnerId,
  fieldName,
  value,
  setFieldValue,
}: ModelFieldProps) {
  const claimableDevices = useClaimableDevices();
  const options = composeModelOptions(claimableDevices, partnerId);
  const isOnlyOneModel = size(options) === 1;

  const previousPartnerId = usePrevious(partnerId);

  useEffect(() => {
    if (previousPartnerId === partnerId) return;

    // If there's only one option, invoke onChange and setFieldValue once
    if (isOnlyOneModel) {
      const firstOptionValue = options[0]?.value;

      setFieldValue(fieldName, firstOptionValue);
    } else {
      setFieldValue(fieldName, '');
    }
  }, [
    fieldName,
    isOnlyOneModel,
    options,
    partnerId,
    previousPartnerId,
    setFieldValue,
  ]);

  return (
    <Select
      withinPortal
      searchable
      data={options}
      mb="md"
      label="Model"
      data-testid="select-model-dropdown"
      name="model"
      placeholder="Select model"
      required
      disabled={isOnlyOneModel}
      readOnly={isOnlyOneModel}
      value={isOnlyOneModel ? options[0]?.value : value}
      onChange={(value) => setFieldValue(fieldName, value)}
    />
  );
}

function composeModelOptions(
  claimableDevices: ReturnType<typeof useClaimableDevices>,
  partnerId: string
) {
  const partnerModelsSorted = claimableDevices
    .filter((device) => device.partner.id === partnerId)
    .sort((a, b) =>
      a?.model?.toLowerCase() > b?.model?.toLowerCase() ? 1 : -1
    );

  return partnerModelsSorted.map((model) => ({
    value: model.id,
    label: model.model,
  }));
}
